import { FunctionComponent, memo, useContext } from "react";
import { IProps } from "./create-tag.types";
import { Button } from "@edgetier/components";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { useQueryClient } from "react-query";
import TagForm from "components-for/tags/tag-form";
import { ISubmittedTag } from "components-for/tags/tag-form/tag-form.types";
import { axios } from "utilities/axios";
import { ITag, ITagsPostRequest, Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { useHistory } from "react-router-dom";
import urljoin from "url-join";
import TagsListContext from "../tags-list-context/tags-list-context";
import { trackUser } from "utilities/track-user";
import "./create-tag.scss";

/**
 * A button that, when clicked, opens a modal with a form to create a new tag.
 * @returns
 */
const CreateTag: FunctionComponent<IProps> = ({ tagGroupId, ...buttonProps }) => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const { closeModal, openModal, modalProps } = useModal();
    const { redirectRoute, tagGroupType } = useContext(TagsListContext);

    /**
     * POST the new tag to the backend.
     * @param values The values of the new tag.
     */
    const onSubmit = async (values: ISubmittedTag) => {
        try {
            const { data } = await axios.post<ITagsPostRequest>(Route.Tags, values);
            queryClient.setQueriesData<ITag[]>(Route.Tags, (tags) => {
                return [...(tags ?? []), { ...values, tagId: data.tagId }];
            });

            toast.success(`${values.tag} has been created successfully.`);
            trackUser("Create Tag");

            history.push(urljoin(redirectRoute, data.tagId));

            closeModal();
        } catch (serverError) {
            toast.error(getServerError(serverError));
        }
    };

    return (
        <div className="create-tag">
            <div className="create-tag__button">
                <Button icon={faPlus} onClick={openModal} styleName="positive" {...buttonProps}>
                    Create Tag
                </Button>
            </div>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>New Tag</h2>
                </ModalHeader>
                <ModalContent>
                    <TagForm
                        initialValues={{ tag: "", tagId: null, tagGroup: "", tagGroupId }}
                        onSubmit={onSubmit}
                        tagGroupType={tagGroupType}
                    />
                </ModalContent>
            </Modal>
        </div>
    );
};

export default memo(CreateTag);
